import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Both breakpoints keep the full product-details grid so the mobile quick-shop
    // shows the same blocks as desktop (badge, rating, color, size guide, etc.).
    // The mobile-specific layout is handled entirely in quick-add-modal-styles.

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#enhanceGallery(modalContent);
    this.#renderReviewBadge(modalContent);
  }

  /**
   * Renders the review app's (Judge.me) preview badge inside the modal.
   *
   * The modal mirrors the PDP, so the injected markup contains the Judge.me
   * preview-badge element — but only as an empty placeholder. Judge.me's own
   * script renders the stars on page load; it never runs against markup we
   * inject afterwards. Worse, on a collection page (where quick-add lives) the
   * page usually has no badges at load, so Judge.me only loads its lightweight
   * `loader.js` and never fetches the widget bundle that does the rendering.
   *
   * So: if the widget bundle is already loaded, re-render the freshly injected
   * badge with Judge.me's own `customizeBadges()` (their documented re-render
   * entry point for AJAX/JS-loaded content). Otherwise re-run Judge.me's loader
   * — with its one-shot guard reset — so it detects the badge, loads the widget
   * bundle and renders it. morph strips the previous product's rendered stars
   * (the fetched source badge is empty), so this re-renders per product.
   * @param {Element} modalContent - The modal content element
   */
  #renderReviewBadge(modalContent) {
    const badge = modalContent.querySelector(
      '.jdgm-preview-badge:not(.jdgm--done-setup), .judgeme-preview-badge:not(.jdgm--done-setup)'
    );
    if (!badge) return;

    const jdgm = /** @type {any} */ (window).jdgm;
    if (!jdgm) return;

    // Bundle already loaded (a badge rendered earlier this session): re-render.
    if (typeof jdgm.customizeBadges === 'function') {
      jdgm.customizeBadges();
      return;
    }

    // Bundle never loaded (collection cards use no Judge.me badge, so only
    // loader.js is present): re-run the loader so it detects the injected badge,
    // fetches the widget bundle and renders it.
    const loader = document.querySelector(
      'script[src*="judgeme"][src*="loader.js"], script[src*="judge-me"][src*="loader.js"]'
    );
    if (!(loader instanceof HTMLScriptElement)) return;

    jdgm._loaderExecuted = false;
    const script = document.createElement('script');
    script.src = loader.src;
    document.head.appendChild(script);
  }

  /**
   * Adds two gallery behaviours the injected markup can't do on its own:
   *  - desktop: hides the scroll-down chevron once the gallery is scrolled
   *  - mobile: builds functional pagination dots for the horizontal chip carousel
   * @param {Element} modalContent - The modal content element
   */
  #enhanceGallery(modalContent) {
    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;

    // Desktop: fade the chevron out once the gallery has been scrolled.
    // Guard with a JS property (survives morph) so we only bind the listener once.
    if (!media._qaScrollBound) {
      media._qaScrollBound = true;
      media.addEventListener(
        'scroll',
        () => media.toggleAttribute('data-scrolled', media.scrollTop > 12),
        { passive: true }
      );
    }

    // Rebuild the dots whenever the gallery changes (e.g. selecting a different
    // colour/variant swaps the media). Observe once; the product-component host
    // is stable across opens.
    if (!modalContent._qaObserver) {
      modalContent._qaObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.#dotsRaf);
        this.#dotsRaf = requestAnimationFrame(() => this.#buildCarouselDots(modalContent));
      });
    }

    this.#buildCarouselDots(modalContent);
  }

  #dotsRaf = 0;

  /**
   * Builds the mobile carousel pagination dots for the current gallery slides.
   * Safe to call repeatedly (removes any previous dots first). The observer is
   * paused while we mutate so appending our own dots doesn't loop it.
   * @param {Element} modalContent - The modal content element
   */
  #buildCarouselDots(modalContent) {
    const observer = modalContent._qaObserver;
    observer?.disconnect();
    try {
      this.#renderCarouselDots(modalContent);
    } finally {
      observer?.observe(modalContent, { childList: true, subtree: true });
    }
  }

  /**
   * @param {Element} modalContent - The modal content element
   */
  #renderCarouselDots(modalContent) {
    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;
    media.querySelector(':scope > .qa-dots')?.remove();
    if (!isMobileBreakpoint()) return;
    const grid = media.querySelector('.media-gallery__grid');
    const slides = grid ? [...grid.querySelectorAll('.product-media-container')] : [];
    if (!grid || slides.length < 2) return;

    const dots = document.createElement('div');
    dots.className = 'qa-dots';
    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'qa-dots__dot';
      dot.setAttribute('aria-label', `${index + 1}`);
      dot.addEventListener('click', () =>
        slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      );
      dots.appendChild(dot);
    });
    media.appendChild(dots);

    // Bind the scroll->active-dot sync once per grid; it re-queries the current
    // dots/slides each time so it stays correct after a rebuild.
    if (!grid._qaDotScroll) {
      grid._qaDotScroll = true;
      grid.addEventListener(
        'scroll',
        () =>
          requestAnimationFrame(() => {
            const dotsEl = media.querySelector(':scope > .qa-dots');
            if (!dotsEl) return;
            const count = grid.querySelectorAll('.product-media-container').length;
            const maxScroll = grid.scrollWidth - grid.clientWidth;
            const ratio = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
            const active = Math.round(ratio * (count - 1));
            [...dotsEl.children].forEach((dot, index) => dot.toggleAttribute('data-active', index === active));
          }),
        { passive: true }
      );
    }
    dots.firstElementChild?.toggleAttribute('data-active', true);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
